// What /welcome says, word for word from GroundTeam/Views/Sync/NoCompanyView.swift (and DeleteAccountButton,
// SyncCenter.invitationCheckFailed) unless noted. Server errors go through auth/errors' ports of
// `TeamSyncMessages.message(for:companyName:)` and `SupabaseError.errorDescription`, so an invite code, a join and a
// create fail with the same sentences as on the iPhone. Pure (unit-tested).

import { DELETE_NO_ANSWER_TEXT, deleteAccountErrorMessage } from '@/auth/accountText';
import { isNetworkError, teamErrorMessage } from '@/auth/errors';

/** The iOS gate's navigation title (CompanyGateView). */
export const PAGE_TITLE = 'Your company';

// MARK: - Invitations for you

/** First load vs. nothing found. */
export function invitationsEmptyTitle(loading: boolean): string {
  return loading ? 'Looking for invitations…' : 'No invitations yet';
}

/**
 * Who to ask. iOS names the iPhone path ("Settings › Team Sync › Team members"); admins can now invite from the
 * console's Team page too, so this says "Team members" only.
 */
export function invitationsEmptyDetail(email: string | null): string {
  if (email) return `Ask your admin to invite ${email} from Team members, or to share an invite code with you.`;
  return 'Invitations sent by email only show up for accounts with that email. Ask your admin for an invite code and enter it below.';
}

/** After "Check again" found nothing. */
export function stillNothingDetail(email: string | null): string {
  if (email) return `Still nothing. Make sure your admin used ${email} (check for typos), or ask them for an invite code.`;
  return 'Still nothing. Ask your admin to share an invite code from Team members.';
}

export const JOIN_HINT = 'Asks you to confirm before joining.';

/**
 * Why the invitations couldn't be checked (SyncCenter.invitationCheckFailed). A request without an answer while the
 * browser says it is online is the server (or the connection to it), which the iPhone can't tell apart; the web can.
 */
export function invitationCheckErrorMessage(e: unknown, online: boolean): string {
  if (isNetworkError(e)) {
    return online
      ? "Couldn't reach the team server. Check your connection, then check again."
      : "You're offline. Connect to the internet, then check again.";
  }
  return teamErrorMessage(e);
}

/** A failed join (list or code): `no_invitation`, `invitation_expired`, `already_member`… worded like iOS. */
export function joinErrorMessage(e: unknown, companyName: string): string {
  return teamErrorMessage(e, companyName);
}

// MARK: - Have an invite code?

export const CODE_HINT = 'Your admin can share a 12-character code with you. It works however you signed in.';
export const CODE_PLACEHOLDER = 'XXXX-XXXX-XXXX';

/** `preview_invite_code` failed: `invalid_code`, `invitation_expired`, or a connection problem. */
export function codeErrorMessage(e: unknown): string {
  return teamErrorMessage(e);
}

// MARK: - Create your company

/** "Start Acme Roofing on your team server. You become its admin and invite your team." */
export function createDescription(companyName: string): string {
  const title = companyName.trim() === '' ? 'your company' : companyName.trim();
  return `Start ${title} on your team server. You become its admin and invite your team.`;
}

export const INVITE_BULLET =
  'Invite teammates right after from Team members. They create their own account and join with your invitation or invite code.';
export const ROLES_BULLET = "Roles are enforced by the server: reps only change what they're allowed to.";

/** `create_company` failed: invalid_input, company_creation_disabled, already_member, or no answer. */
export function createErrorMessage(e: unknown): string {
  return teamErrorMessage(e);
}

/** The seed couldn't be assembled (a bug in this build): nothing is created. */
export const SEED_BUILD_FAILED_TEXT = "Ground Team couldn't prepare the company's defaults, so nothing was created. Reload the page and try again.";

export const FINISHING_TITLE = 'Finishing setup…';

/** Under "Finishing setup…" (web wording: iOS uploads its local copy silently). */
export function finishingDetail(companyName: string): string {
  return `${companyName} was created. Ground Team is saving its pipelines, visit results, templates and settings to the team server.`;
}

// MARK: - Opening the company

export function joinedTitle(companyName: string): string {
  return `You joined ${companyName}`;
}

export function createdTitle(companyName: string): string {
  return `${companyName} is ready`;
}

export const OPENING_TEXT = 'Opening Ground Team…';

export function openFailedText(companyName: string): string {
  return `Couldn't open ${companyName} yet.`;
}

// MARK: - Account

export const DELETE_CAPTION = 'Deleting your account removes your sign-in permanently.';

/** DeleteAccountButton without a company: the confirmation and its button. */
export function deleteAccountConfirmation(account: string | null): { title: string; message: string; confirmLabel: string } {
  return {
    title: 'Delete your account?',
    message: `Your sign-in (${account ?? 'this account'}) is deleted from the team server. This cannot be undone.`,
    confirmLabel: 'Delete my account permanently',
  };
}

/** DeleteAccountButton.deletedTitle / deletedMessage, shown on the sign-in screen afterwards. */
export const ACCOUNT_DELETED_NOTICE = { title: 'Account deleted', message: 'Your Ground Team account was deleted.' } as const;

/**
 * Why deleting failed: the same rules as the Account page (no answer while online → DELETE_NO_ANSWER_TEXT; offline;
 * anything else like iOS).
 */
export function deleteErrorMessage(e: unknown, online: boolean): string {
  return deleteAccountErrorMessage(e, null, online);
}

export { DELETE_NO_ANSWER_TEXT };
